import { useEffect, useState } from "react";
import type { FieldContent } from "@capture/shared";
import {
  DATE_KEY_STANDARDS,
  CUSTOM_DATE_PATTERN_VALUE,
  CUSTOM_DATE_TOKENS,
  describeCustomDatePattern,
  isValidCustomDatePattern,
} from "@capture/shared";
import { useFormConfigStore } from "../../state/store";
import { FieldRow, FieldCheckboxRow, blurOnEnter, emptyToUndefined } from "./Field";

/** A stored `keyStandard` carrying a custom token pattern (`CUSTOM:<PATTERN>`). */
const CUSTOM_PREFIX = `${CUSTOM_DATE_PATTERN_VALUE}:`;

/**
 * Display section — columnSpan, placeholder, helpText, inputMask, and
 * (modal-redesign WU-C 2026-05-10) a numberFormat sub-form for
 * number / decimal / currency dataTypes.
 *
 * `inputMask` is conditional on dataType being `text` or `number`.
 * Currency uses numberFormat (now wired up here in WU-C; was deferred
 * pre-WU-C), not inputMask — see design doc edge cases.
 */

const COLUMN_SPANS: ReadonlyArray<1 | 2 | 3> = [1, 2, 3];

const TYPES_WITH_INPUT_MASK: ReadonlySet<FieldContent["dataType"]> = new Set([
  "text",
  "number",
]);

const TYPES_WITH_NUMBER_FORMAT: ReadonlySet<FieldContent["dataType"]> = new Set([
  "number",
  "decimal",
  "currency",
]);

type NumberFormat = NonNullable<FieldContent["numberFormat"]>;

const NEGATIVE_STYLE_OPTIONS: ReadonlyArray<{
  value: NonNullable<NumberFormat["negativeStyle"]>;
  label: string;
}> = [
  { value: "minus", label: "Minus (-1234.56)" },
  { value: "parens", label: "Parens ((1234.56))" },
];

export function DisplaySection() {
  const content = useFormConfigStore((s) => s.content);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const updateField = useFormConfigStore((s) => s.updateField);

  const field = content?.fields.find((f) => f.name === selectedFieldName) ?? null;

  const [placeholderDraft, setPlaceholderDraft] = useState(field?.placeholder ?? "");
  const [helpTextDraft, setHelpTextDraft] = useState(field?.helpText ?? "");
  const [inputMaskDraft, setInputMaskDraft] = useState(field?.inputMask ?? "");
  const [decimalsDraft, setDecimalsDraft] = useState(
    field?.numberFormat?.decimals != null ? String(field.numberFormat.decimals) : "",
  );
  const [currencySymbolDraft, setCurrencySymbolDraft] = useState(
    field?.numberFormat?.currencySymbol ?? "",
  );
  // E-025 — draft for the "Other (custom pattern)" token string. Seeded from a
  // stored `CUSTOM:<PATTERN>` keyStandard; only persisted when it parses.
  const storedPattern =
    field?.keyStandard?.startsWith(CUSTOM_PREFIX)
      ? field.keyStandard.slice(CUSTOM_PREFIX.length)
      : "";
  const [customPatternDraft, setCustomPatternDraft] = useState(storedPattern);
  // Whether the date Input-format picker is on the custom option (either the
  // bare CUSTOM option just selected, or a stored CUSTOM:<PATTERN>).
  const [customSelected, setCustomSelected] = useState(storedPattern !== "");

  useEffect(() => {
    setPlaceholderDraft(field?.placeholder ?? "");
    setHelpTextDraft(field?.helpText ?? "");
    setInputMaskDraft(field?.inputMask ?? "");
    setDecimalsDraft(
      field?.numberFormat?.decimals != null ? String(field.numberFormat.decimals) : "",
    );
    setCurrencySymbolDraft(field?.numberFormat?.currencySymbol ?? "");
    const sp = field?.keyStandard?.startsWith(CUSTOM_PREFIX)
      ? field.keyStandard.slice(CUSTOM_PREFIX.length)
      : "";
    setCustomPatternDraft(sp);
    setCustomSelected(sp !== "");
  }, [
    field?.fieldDefinitionId,
    field?.placeholder,
    field?.helpText,
    field?.inputMask,
    field?.numberFormat?.decimals,
    field?.numberFormat?.currencySymbol,
    field?.keyStandard,
  ]);

  if (!field || !content) return null;

  function commitColumnSpan(next: number) {
    if (!field) return;
    if (next !== 1 && next !== 2 && next !== 3) return;
    if (next === field.columnSpan) return;
    updateField(field.name, { columnSpan: next });
  }

  function commitPlaceholder() {
    if (!field) return;
    const next = emptyToUndefined(placeholderDraft);
    if (next === field.placeholder) return;
    updateField(field.name, { placeholder: next });
  }

  function commitHelpText() {
    if (!field) return;
    const next = emptyToUndefined(helpTextDraft);
    if (next === field.helpText) return;
    updateField(field.name, { helpText: next });
  }

  function commitInputMask() {
    if (!field) return;
    const next = emptyToUndefined(inputMaskDraft);
    if (next === field.inputMask) return;
    updateField(field.name, { inputMask: next });
  }

  // E-021 — per-field INPUT key standard for date fields. Empty = inherit the
  // job-type/tenant default (persist null so the resolver falls back). A set
  // value tells the VS to canonicalize separatorless keyed digits on commit.
  function commitKeyStandard(next: string) {
    if (!field) return;
    // E-025 — the "Other (custom pattern)" option reveals the pattern input but
    // does NOT itself persist a keyStandard until a VALID pattern is entered
    // (an empty/invalid pattern would canonicalize nothing). Toggle the UI only.
    if (next === CUSTOM_DATE_PATTERN_VALUE) {
      setCustomSelected(true);
      return;
    }
    setCustomSelected(false);
    const value = next === "" ? null : next;
    if (value === (field.keyStandard ?? null)) return;
    updateField(field.name, { keyStandard: value });
  }

  // E-025 — persist a custom token pattern as `CUSTOM:<PATTERN>` only when it
  // passes the token-grammar parser. An invalid pattern is never stored
  // (free-text alone is never shipped); the preview shows why.
  function commitCustomPattern() {
    if (!field) return;
    const pattern = customPatternDraft.trim().toUpperCase();
    if (pattern === "") return;
    // Validate via the single grammar gate (same one the preview renders).
    // Invalid patterns are never persisted — free-text alone is never shipped.
    if (!isValidCustomDatePattern(pattern)) return;
    const value = `${CUSTOM_PREFIX}${pattern}`;
    if (value === field.keyStandard) return;
    updateField(field.name, { keyStandard: value });
  }

  /**
   * NumberFormat sub-form helpers — `mergeNumberFormat` is the single
   * write path. It collapses an empty resulting object to `undefined`
   * so the snapshot/persistence layer never carries an empty
   * `{ decimals: undefined }` shell.
   */
  function mergeNumberFormat(patch: Partial<NumberFormat>) {
    if (!field) return;
    const merged: NumberFormat = { ...(field.numberFormat ?? {}), ...patch };
    // Drop undefined keys so the resulting object only carries set values.
    const cleaned: NumberFormat = {};
    if (merged.decimals != null) cleaned.decimals = merged.decimals;
    if (merged.thousandsSeparator != null) cleaned.thousandsSeparator = merged.thousandsSeparator;
    if (merged.currencySymbol != null) cleaned.currencySymbol = merged.currencySymbol;
    if (merged.negativeStyle != null) cleaned.negativeStyle = merged.negativeStyle;
    const next = Object.keys(cleaned).length > 0 ? cleaned : undefined;
    updateField(field.name, { numberFormat: next });
  }

  function commitDecimals() {
    if (!field) return;
    const trimmed = decimalsDraft.trim();
    if (trimmed === "") {
      mergeNumberFormat({ decimals: undefined });
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) return;
    if (n === field.numberFormat?.decimals) return;
    mergeNumberFormat({ decimals: n });
  }

  function toggleThousandsSeparator(next: boolean) {
    mergeNumberFormat({ thousandsSeparator: next || undefined });
  }

  function commitCurrencySymbol() {
    if (!field) return;
    const next = emptyToUndefined(currencySymbolDraft);
    if (next === field.numberFormat?.currencySymbol) return;
    mergeNumberFormat({ currencySymbol: next });
  }

  function commitNegativeStyle(next: NonNullable<NumberFormat["negativeStyle"]>) {
    if (!field) return;
    if (next === field.numberFormat?.negativeStyle) return;
    mergeNumberFormat({ negativeStyle: next });
  }

  const showInputMask = TYPES_WITH_INPUT_MASK.has(field.dataType);
  const showNumberFormat = TYPES_WITH_NUMBER_FORMAT.has(field.dataType);
  const showCurrencySymbol = field.dataType === "currency";
  // E-021 — per-field INPUT format for date fields (separatorless fast keying).
  const showDateInputFormat = field.dataType === "date";

  return (
    <section className="field-section" aria-label="Display">
      <h3 className="field-section__title">Display</h3>

      <FieldRow
        htmlFor="disp-column-span"
        label="Column span"
        hint="How many columns the input occupies in the key-entry grid."
      >
        <select
          id="disp-column-span"
          className="field-section__input"
          value={field.columnSpan ?? 1}
          onChange={(e) => commitColumnSpan(Number(e.target.value))}
        >
          {COLUMN_SPANS.map((cs) => (
            <option key={cs} value={cs}>{cs}</option>
          ))}
        </select>
      </FieldRow>

      <FieldRow
        htmlFor="disp-placeholder"
        label="Placeholder"
        hint="Hint text shown inside the input."
      >
        <input
          id="disp-placeholder"
          type="text"
          className="field-section__input"
          value={placeholderDraft}
          onChange={(e) => setPlaceholderDraft(e.target.value)}
          onBlur={commitPlaceholder}
          onKeyDown={blurOnEnter}
        />
      </FieldRow>

      <FieldRow
        htmlFor="disp-help-text"
        label="Help text"
        hint="Inline guidance shown beneath the input."
      >
        <input
          id="disp-help-text"
          type="text"
          className="field-section__input"
          value={helpTextDraft}
          onChange={(e) => setHelpTextDraft(e.target.value)}
          onBlur={commitHelpText}
          onKeyDown={blurOnEnter}
        />
      </FieldRow>

      {showInputMask && (
        <FieldRow
          htmlFor="disp-input-mask"
          label="Input mask"
          hint={
            <>
              Guided entry pattern. <code>9</code> = digit, <code>A</code> = letter,{" "}
              <code>*</code> = any. Example: <code>(999) 999-9999</code> for US phone,{" "}
              <code>999-99-9999</code> for SSN.
            </>
          }
        >
          <input
            id="disp-input-mask"
            type="text"
            className="field-section__input"
            value={inputMaskDraft}
            onChange={(e) => setInputMaskDraft(e.target.value)}
            onBlur={commitInputMask}
            onKeyDown={blurOnEnter}
          />
        </FieldRow>
      )}

      {showDateInputFormat && (
        <FieldRow
          htmlFor="disp-key-standard"
          label="Input format"
          hint={
            <>
              How operators KEY this date — independent of the export format.
              Separatorless options (e.g. <code>MMDDYYYY</code>) let operators
              type 8 digits with no slashes for faster keying; the value is
              canonicalized on commit and formatted for export separately.
              Empty = inherit the job type's default.
            </>
          }
        >
          <select
            id="disp-key-standard"
            className="field-section__input"
            value={customSelected ? CUSTOM_DATE_PATTERN_VALUE : field.keyStandard ?? ""}
            onChange={(e) => commitKeyStandard(e.target.value)}
          >
            <option value="">Default (MM/DD/YYYY with separators)</option>
            {DATE_KEY_STANDARDS.map((ks) => (
              <option key={ks.value} value={ks.value}>
                {ks.label}
              </option>
            ))}
          </select>
        </FieldRow>
      )}

      {showDateInputFormat && customSelected && (
        <FieldRow
          htmlFor="disp-key-standard-custom"
          label="Custom pattern"
          hint={
            <>
              Build the input order from these tokens only:{" "}
              {CUSTOM_DATE_TOKENS.map((t, i) => (
                <span key={t}>
                  {i > 0 ? " " : ""}
                  <code>{t}</code>
                </span>
              ))}{" "}
              (<code>C</code> = century flag, <code>JJJ</code> = day-of-year).
              Needs a year and either <code>DD</code> or <code>JJJ</code>. Unknown
              or ambiguous tokens are rejected.
            </>
          }
        >
          <input
            id="disp-key-standard-custom"
            type="text"
            className="field-section__input"
            value={customPatternDraft}
            placeholder="e.g. YYYYJJJ"
            spellCheck={false}
            onChange={(e) => setCustomPatternDraft(e.target.value)}
            onBlur={commitCustomPattern}
            onKeyDown={blurOnEnter}
          />
          <p
            className="field-section__hint"
            data-testid="custom-pattern-preview"
          >
            {describeCustomDatePattern(customPatternDraft.trim().toUpperCase())}
          </p>
        </FieldRow>
      )}

      {showNumberFormat && (
        <>
          <h4 className="field-section__subtitle">Number format</h4>
          <p className="field-section__group-hint">
            Controls how operators see numbers and how they render at export.
            <code> decimals</code> = digits after the point;{" "}
            <code>thousandsSeparator</code> renders <code>1,234</code>;{" "}
            <code>currencySymbol</code> prefixes (<code>$</code>, <code>€</code>);{" "}
            <code>negativeStyle</code> picks <code>-1234</code> vs <code>(1234)</code>.
          </p>

          <FieldRow
            htmlFor="disp-decimals"
            label="Decimals"
            hint={
              <>
                Digits after the point at export. Example: <code>2</code> renders{" "}
                <code>1234.50</code>. Empty = no formatting.
              </>
            }
          >
            <input
              id="disp-decimals"
              type="number"
              min={0}
              className="field-section__input"
              value={decimalsDraft}
              onChange={(e) => setDecimalsDraft(e.target.value)}
              onBlur={commitDecimals}
              onKeyDown={blurOnEnter}
            />
          </FieldRow>

          <FieldCheckboxRow
            id="disp-thousands"
            checked={field.numberFormat?.thousandsSeparator ?? false}
            label="Thousands separator"
            hint="Render 1,234,567 instead of 1234567 (uses comma in en-US locale)."
            onChange={toggleThousandsSeparator}
          />

          {showCurrencySymbol && (
            <FieldRow
              htmlFor="disp-currency-symbol"
              label="Currency symbol"
              hint={
                <>
                  Prefix character(s). Examples: <code>$</code>, <code>€</code>,{" "}
                  <code>USD </code> (trailing space is significant).
                </>
              }
            >
              <input
                id="disp-currency-symbol"
                type="text"
                className="field-section__input"
                value={currencySymbolDraft}
                onChange={(e) => setCurrencySymbolDraft(e.target.value)}
                onBlur={commitCurrencySymbol}
                onKeyDown={blurOnEnter}
              />
            </FieldRow>
          )}

          <FieldRow
            htmlFor="disp-negative-style"
            label="Negative style"
            hint={
              <>
                How negative numbers render at export — minus sign (<code>-1234</code>)
                or accounting parens (<code>(1234)</code>).
              </>
            }
          >
            <select
              id="disp-negative-style"
              className="field-section__input"
              value={field.numberFormat?.negativeStyle ?? "minus"}
              onChange={(e) =>
                commitNegativeStyle(
                  e.target.value as NonNullable<NumberFormat["negativeStyle"]>,
                )
              }
            >
              {NEGATIVE_STYLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FieldRow>
        </>
      )}
    </section>
  );
}
